import {useState, useRef, useEffect} from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    TextField,
    IconButton,
    Avatar,
    Paper,
    InputAdornment,
    Chip, Divider
} from "@mui/material"
import {Client} from "@stomp/stompjs"
import {
    Close as CloseIcon,
    Send as SendIcon,
    AttachFile as AttachFileIcon,
    EmojiEmotions as EmojiIcon,
    Phone as PhoneIcon,
    VideoCall as VideoCallIcon,
    MoreVert as MoreVertIcon,
    Done as DoneIcon,
    DoneAll as DoneAllIcon,
} from "@mui/icons-material"
import {config} from "../config/environment";
import Cookies from "js-cookie";
import axiosInstance from "../config/axios-config";
import {formatDistanceToNow} from "date-fns"
import {ru, hy} from "date-fns/locale";
import {useTranslation} from "react-i18next";
import i18n from "../i18n/i18n";


const ChatPopup = ({open, onClose, conversation, onTyping}) => {
    const [newText, setNewText] = useState("")
    const [messages, setMessages] = useState([])
    const messagesEndRef = useRef(null)
    const [hasMore, setHasMore] = useState(true)
    const scrollContainerRef = useRef(null)
    const [page, setPage] = useState(0)
    const {t} = useTranslation()
    const [loading, setLoading] = useState(false);


    const [stompClient, setStompClient] = useState(null)
    const [isConnected, setIsConnected] = useState(false)
    const stompClientRef = useRef(null)

    const tenantId = Cookies.get("currentTenantId")

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: "smooth"})
    }

    const getLocale = () => {
        switch (i18n.language) {
            case "ru":
                return ru;
            case "hy":
                return hy;
            default:
                return undefined; // defaults to English
        }
    };

    useEffect(() => {
        scrollToBottom()
    }, [messages])

    const getMessages = async (pageToLoad, replace = false) => {
        if (loading) return;
        setLoading(true);

        try {
            const response = await axiosInstance.get(`/api/v1/chat-messages/${conversation.conversationId}`, {
                params: {
                    page: pageToLoad,
                    size: 50
                }
            });
            const data = response.data.content;

            if (data.length < 50) setHasMore(false);

            if (replace) {
                setMessages(data);
                setTimeout(() => scrollToBottom(), 100);
            } else {
                // preserve scroll position before adding older messages
                const scrollHeightBefore = scrollContainerRef.current.scrollHeight;

                setMessages((prev) => [...data, ...prev]);

                setTimeout(() => {
                    const scrollHeightAfter = scrollContainerRef.current.scrollHeight;
                    scrollContainerRef.current.scrollTop =
                        scrollHeightAfter - scrollHeightBefore;
                }, 100);
            }
        } finally {
            setLoading(false);
        }

    }

    useEffect(() => {
        if (conversation?.conversationId) {
            setMessages([]);
            setPage(0);
            setHasMore(true);
            getMessages(0, true).then(r => console.log("Messages loaded"))
        }
    }, [conversation]);


    const handleScroll = () => {
        const container = scrollContainerRef.current;
        if (!container || loading || !hasMore) return;

        if (container.scrollTop === 0) {
            // Reached top → load older messages
            const nextPage = page + 1;
            setPage(nextPage);
            getMessages(nextPage).then(r => console.log("Older messages loaded"));
        }
    };

    useEffect(() => {

        // Setup WebSocket connection
        const client = new Client({
            brokerURL: `${config.WEBSOCKET_URL}/ws`,
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            onConnect: () => {
                console.log("Connected to WebSocket")
                setIsConnected(true)

                // Subscribe to messages
                client.subscribe(`/queue/conversations/${conversation.conversationId}/user/${tenantId}`, (message) => {
                    const receivedMessage = JSON.parse(message.body)
                    setMessages((prevMessages) => [...prevMessages, receivedMessage])
                })

                // Subscribe to read status updates
                client.subscribe(`/queue/conversations/${conversation.conversationId}/read-status`, (message) => {
                    const readStatusUpdate = JSON.parse(message.body)
                    // Update the read status of messages in real-time
                    setMessages((prevMessages) => {
                        return prevMessages.map((msg) =>
                            msg.messageId?.toString() === readStatusUpdate.messageId?.toString()
                                ? readStatusUpdate
                                : msg
                        );
                    });
                })
            },
            onDisconnect: () => {
                console.log("Disconnected from WebSocket")
                setIsConnected(false)
            },
            onStompError: (frame) => {
                console.error("STOMP Error:", frame)
                setIsConnected(false)
            },
            onWebSocketError: (error) => {
                console.error("WebSocket Error:", error)
                setIsConnected(false)
            },
        })

        client.activate()
        setStompClient(client)
        stompClientRef.current = client

        return () => {
            if (client) {
                client.deactivate().then(r => console.log("WebSocket connection closed"))
                setIsConnected(false)
            }
        }
    }, [conversation?.conversationId])

    const sendText = (text) => {
        if (!stompClient || !conversation) {
            console.warn("WebSocket not connected or no conversation selected")
            return
        }
        stompClient.publish({
            destination: `/app/queue/conversations/${conversation.conversationId}`,
            body: JSON.stringify(createMessage(text)),
        });
    }

    function createMessage(text) {
        const recipientId = getRecipientId(conversation)
        return {
            text: text,
            senderId: tenantId,
            createdAt: new Date().toISOString(),
            conversationId: conversation.conversationId,
            recipientId: recipientId,
            messageType: "TEXT",
            isRead: false
        }
    }

    function getRecipientId(conversation) {
        if (!conversation)
            return null
        return conversation.participantOneId === tenantId ? conversation.participantTwoId : conversation.participantOneId
    }

    const handleSendMessage = () => {
        if (newText.trim()) {
            sendText(newText)
            setNewText("")
        }
    }

    const handleKeyPress = (e) => {
        // Check if Enter is pressed without Shift
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();

            // Use local trimmed value instead of relying on possibly stale state
            const message = newText.trim();

            // Don't send if message is empty
            if (message.length === 0) return;

            sendText(message);
            setNewText("");
        }
    }

    const formatDateLabel = (timestamp) => {
        const messageDate = new Date(timestamp)
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        // Reset time to compare only dates
        messageDate.setHours(0, 0, 0, 0)
        today.setHours(0, 0, 0, 0)
        yesterday.setHours(0, 0, 0, 0)

        if (messageDate.getTime() === today.getTime()) {
            return "Today"
        } else if (messageDate.getTime() === yesterday.getTime()) {
            return "Yesterday"
        } else {
            return messageDate.toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
            })
        }
    }

    const isDifferentDay = (timestamp1, timestamp2) => {
        const date1 = new Date(timestamp1)
        const date2 = new Date(timestamp2)

        return (
            date1.getDate() !== date2.getDate() ||
            date1.getMonth() !== date2.getMonth() ||
            date1.getFullYear() !== date2.getFullYear()
        )
    }

    const formatMessageTime = (timestamp) => {
        try {
            const date = new Date(timestamp)
            return date.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})
        } catch (error) {
            return ""
        }
    }
    const addressToString = (address) => {
        if (!address) return "";
        return `${address.street || ""}, ${address.city || ""}, ${address.country || ""}`.trim();
    }

    if (!conversation) return null

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: "80vh",
                    maxHeight: "600px",
                    borderRadius: 3,
                    overflow: "hidden",
                },
            }}
        >
            {/* Chat Header */}
            <DialogTitle
                sx={{
                    p: 2,
                    background: "linear-gradient(to right, #f5f7fa, #e4e8f0)",
                    borderBottom: 1,
                    borderColor: "divider",
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                    {/* LEFT SECTION */}
                    <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                        <Avatar src={conversation.senderAvatar} sx={{width: 40, height: 40}}>
                            {conversation.sender?.tenantNameEn?.charAt(0)}
                        </Avatar>
                        <Box>
                            {/* Tenant name */}
                            <Typography variant="h6" sx={{fontWeight: 600}}>
                                {conversation.sender?.tenantNameEn}
                            </Typography>
                            {/* Address */}
                            {conversation.sender?.address && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.2,
                                        maxWidth: 220,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        mb: 0.5,
                                    }}
                                >
                                    {addressToString(conversation.sender.address)}
                                </Typography>
                            )}

                            {/* Status & last seen */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mt: 0.3,
                                }}
                            >
                                {/*{conversation.isOnline ? (*/}
                                {/*    <Chip*/}
                                {/*        label="Online"*/}
                                {/*        size="small"*/}
                                {/*        color="success"*/}
                                {/*        sx={{height: 18, fontSize: "0.7rem"}}*/}
                                {/*    />*/}
                                {/*) : (*/}
                                {/*    <Chip*/}
                                {/*        label="Offline"*/}
                                {/*        size="small"*/}
                                {/*        color="default"*/}
                                {/*        sx={{height: 18, fontSize: "0.7rem"}}*/}
                                {/*    />*/}
                                {/*)}*/}

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontStyle: "italic",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: 250,
                                    }}
                                >
                                    {conversation.lastMessageTimestamp
                                        ? `${t("LastSeen")} ${formatDistanceToNow(
                                            new Date(conversation.lastMessageTimestamp),
                                            {addSuffix: true, locale: getLocale()}
                                        )}`
                                        : ""}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    {/* RIGHT SECTION */}
                    <Box sx={{display: "flex", gap: 1}}>
                        {/*<IconButton size="small">*/}
                        {/*    <PhoneIcon/>*/}
                        {/*</IconButton>*/}
                        {/*<IconButton size="small">*/}
                        {/*    <VideoCallIcon/>*/}
                        {/*</IconButton>*/}
                        <IconButton size="small">
                            <MoreVertIcon/>
                        </IconButton>
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            {/* Messages Area */}
            <DialogContent
                sx={{
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    bgcolor: "#f8f9fa",
                }}
            >
                <Box
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        p: 2,
                        "&::-webkit-scrollbar": {
                            width: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: "rgba(0,0,0,0.2)",
                            borderRadius: "3px",
                        },
                    }}
                >
                    {loading && page > 0 && (
                        <Typography align="center" variant="caption" color="text.secondary" sx={{my: 1}}>
                            {t("LoadingOlderMessages")}
                        </Typography>
                    )}
                    {messages.map((message, index) => (
                        <Box key={message.id}>
                            {(index === 0 || isDifferentDay(messages[index - 1].createdAt, message.createdAt)) && (
                                <Box sx={{display: "flex", alignItems: "center", my: 3}}>
                                    <Divider sx={{flex: 1}}/>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            mx: 2,
                                            px: 2,
                                            py: 0.5,
                                            bgcolor: "white",
                                            borderRadius: 2,
                                            color: "text.secondary",
                                            fontWeight: 500,
                                            fontSize: "0.75rem",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        {formatDateLabel(message.createdAt)}
                                    </Typography>
                                    <Divider sx={{flex: 1}}/>
                                </Box>
                            )}

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: message.senderId === tenantId ? "flex-end" : "flex-start",
                                    mb: 2,
                                }}
                            >
                                <Paper
                                    sx={{
                                        p: 2,
                                        maxWidth: "70%",
                                        bgcolor: message.senderId === tenantId ? "primary.main" : "white",
                                        color: message.senderId === tenantId ? "white" : "text.primary",
                                        borderRadius: message.senderId === tenantId ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mb: 0.5,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                        }}
                                    >
                                        {message.text}
                                    </Typography>

                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                opacity: 0.7,
                                                fontSize: "0.7rem",
                                            }}
                                        >
                                            {formatMessageTime(message.createdAt)}
                                        </Typography>
                                        {message.senderId === tenantId &&
                                            (message.isRead ? (
                                                <DoneAllIcon sx={{fontSize: "0.9rem", opacity: 0.7}}/>
                                            ) : (
                                                <DoneIcon sx={{fontSize: "0.9rem", opacity: 0.7}}/>
                                            ))}
                                    </Box>
                                </Paper>
                            </Box>
                        </Box>
                    ))}
                    <div ref={messagesEndRef}/>
                </Box>

                {/* Message Input */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: 1,
                        borderColor: "divider",
                        bgcolor: "white",
                    }}
                >
                    <TextField
                        fullWidth
                        multiline
                        maxRows={3}
                        value={newText}
                        onChange={(e) => setNewText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={t("TypeYourMessage")}
                        onClick={onTyping}
                        variant="outlined"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                                bgcolor: "#f8f9fa",
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton size="small">
                                        <AttachFileIcon/>
                                    </IconButton>
                                    <IconButton size="small">
                                        <EmojiIcon/>
                                    </IconButton>
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton color="primary" onClick={handleSendMessage} disabled={!newText.trim()}>
                                        <SendIcon/>
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default ChatPopup
